import { Link } from "wouter";
import { ClickableCard } from "@/components/property/clickable-card";
import { LastMinuteOffer } from "@/components/promotions/last-minute-offer";
import { useQuery } from "@tanstack/react-query";
import type { PublicProperty } from "@shared/schema";
import { useBooking } from "@/hooks/use-booking-context";
import { RateLine } from "@/components/property/rate-line";
import { Button } from "@/components/ui/button";
import { TrustLine } from "@/components/property/trust-line";
import { NextOpenWindows } from "@/components/property/next-open-windows";
import { NewListingBadge } from "@/components/property/new-listing-badge";

const img = (src: string, w: number) => `/img?src=${encodeURIComponent(src)}&w=${w}&q=90`;

/**
 * Both homes as the same cards Our Homes uses, for the sidebars of shared
 * pages (travel guide and the like) that used to embed the first home's
 * calendar. Dates already chosen ride along to the home's booking page.
 */
export function HomesPicker({ className = "", layout = "stack", headingLevel = 2 }: { className?: string; layout?: "stack" | "grid"; headingLevel?: 2 | 3 }) {
  // A card title is a section of the page it sits on: an h2 under the page
  // heading, an h3 inside a sidebar section that already has its own heading.
  const Heading = (headingLevel === 3 ? "h3" : "h2") as "h2" | "h3";
  const { data: homes = [] } = useQuery<PublicProperty[]>({ queryKey: ["/api/properties"] });
  const { checkIn, checkOut } = useBooking();
  const listed = homes.filter((h) => h.slug);
  if (listed.length === 0) return null;

  const params = new URLSearchParams();
  if (checkIn) params.set("start", checkIn);
  if (checkOut) params.set("end", checkOut);
  params.set("open", "dates");
  const qs = params.toString();

  return (
    <ul className={`${layout === "grid" ? "grid gap-5 md:grid-cols-2" : "space-y-5"} ${className}`} data-testid="homes-picker">
      {listed.map((home) => {
        const cover =
          home.hero_image_url || [...(home.photos || [])].sort((a, b) => a.sort_order - b.sort_order)[0]?.url;
        const href = `/properties/${home.slug}`;
        return (
          <ClickableCard key={home.id} href={href} className="group flex flex-col overflow-hidden border border-sand-deep bg-sand transition-shadow hover:shadow-lg" data-testid={`homes-picker-${home.slug}`}>
            <Link href={href} className="block">
              <div className="relative aspect-[3/2] overflow-hidden bg-sand">
                <NewListingBadge slug={home.slug} variant="overlay" />
                {cover && (
                  <img
                    src={img(cover, 640)}
                    alt={`${home.title}, ${home.public_location_label || home.general_location}`}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </div>
            </Link>
            <div className="flex flex-1 flex-col p-5">
              <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-driftwood">{home.public_location_label || home.general_location}</p>
              <Heading className="mt-2 font-serif text-2xl text-ink">
                <Link href={href} className="transition-colors hover:text-ink/70">{home.title}</Link>
              </Heading>
              <p className="mt-2 font-sans text-luxury-sm text-ink/70">
                {home.bedrooms} bedrooms · {home.bathrooms} bathrooms · up to {home.max_guests} guests
              </p>
              <TrustLine home={home} className="mt-2" />
              <LastMinuteOffer slug={home.slug} variant="badge" className="mt-3 self-start" />
              <NextOpenWindows slug={home.slug} className="mt-2" />
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <RateLine home={home} size="sm" />
                <Link href={`${href}/book?${qs}`} data-primary-book-cta data-testid={`homes-picker-book-${home.slug}`}>
                  <Button className="bg-ink font-sans tracking-wide text-shell hover:bg-ink/90">Check dates</Button>
                </Link>
              </div>
            </div>
          </ClickableCard>
        );
      })}
    </ul>
  );
}
